import React, { useState } from 'react';
import { StickyNote, Plus, Edit2, Trash2, X, ChevronDown, ChevronUp } from 'lucide-react';

export default function BandNotesSection({ bandNotes, onSaveNote, onDeleteNote }) {
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditingNote(null); setNoteTitle(''); setNoteContent(''); setShowModal(true);
  };

  const openEdit = (note) => {
    setEditingNote(note); setNoteTitle(note.title); setNoteContent(note.content); setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false); setEditingNote(null); setNoteTitle(''); setNoteContent('');
  };

  const handleSave = async () => {
    if (!noteTitle.trim() || !noteContent.trim()) return;
    try {
      setSaving(true);
      await onSaveNote({ title: noteTitle.trim(), content: noteContent.trim() }, editingNote);
      closeModal();
    } catch (err) {
      // Error handled by parent
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="card-3d rounded-card overflow-hidden">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
        >
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-white" />
            Band Notes
            {bandNotes.length > 0 && (
              <span className="bg-white/10 text-white text-xs px-2 py-0.5 rounded-full">{bandNotes.length}</span>
            )}
          </h2>
          {expanded ? <ChevronUp className="w-5 h-5 text-text-subtle" /> : <ChevronDown className="w-5 h-5 text-text-subtle" />}
        </button>

        {expanded && (
          <div className="border-t border-surface-border-light p-4 sm:p-6">
            <p className="text-sm text-text-subtle mb-4">
              Shared notes for your band — meeting minutes, ideas, decisions, to-do lists.
            </p>

            <button
              onClick={openNew}
              className="bg-brand hover:bg-brand-hover transition px-4 py-2 font-semibold text-sm flex items-center gap-2 mb-4 rounded-pill"
            >
              <Plus className="w-4 h-4" /> New Note
            </button>

            {bandNotes.length === 0 ? (
              <p className="text-text-subtle text-sm">No notes yet. Create one to get started!</p>
            ) : (
              <div className="space-y-3">
                {bandNotes.map(note => (
                  <div key={note.id} className="border border-surface-border-light rounded-card-btn p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-semibold text-white">{note.title}</h3>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => openEdit(note)} className="bg-transparent hover:bg-brand transition p-2 rounded-full" title="Edit">
                          <Edit2 className="w-3 h-3 text-white" />
                        </button>
                        <button onClick={() => onDeleteNote(note.id)} className="bg-transparent hover:bg-red-600 transition p-2 rounded-full" title="Delete">
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-text-subtle mb-3">
                      By {note.createdByName} • {new Date(note.createdAt).toLocaleDateString('nl-NL')}
                      {note.updatedAt && note.updatedAt !== note.createdAt && (
                        <span> • Edited {new Date(note.updatedAt).toLocaleDateString('nl-NL')}</span>
                      )}
                    </p>
                    <p className="text-sm text-text-muted whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Note Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card-3d border border-surface-border-light rounded-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingNote ? 'Edit Note' : 'New Note'}</h3>
              <button onClick={closeModal} className="text-text-subtle hover:text-white transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-2 block">Title</label>
                <input
                  type="text" value={noteTitle} onChange={(e) => setNoteTitle(e.target.value)}
                  placeholder="Note title..."
                  className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-subtle mb-2 block">Content</label>
                <textarea
                  value={noteContent} onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Write your note here..." rows={8}
                  className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand resize-none rounded-card"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !noteTitle.trim() || !noteContent.trim()}
                  className="flex-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-6 py-3 font-semibold rounded-pill"
                >
                  {saving ? 'Saving...' : (editingNote ? 'Save Changes' : 'Create Note')}
                </button>
                <button onClick={closeModal} className="bg-white/10 hover:bg-white/20 transition px-6 py-3 font-semibold rounded-pill">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
